// Help Centre customer login (scoped to the help centre's org). Includes a
// "Forgot password?" flow that reuses the customer set-password link.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useHelpCentre } from './HelpCentreLayout';
import { Eye, EyeSlash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function HelpCentreLoginPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { reloadCustomer, help_centre: hc, customer } = useHelpCentre();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const theme = hc.branding?.theme_color || '#2563eb';

  useEffect(() => {
    if (customer) navigate(`/helpcentre/${slug}/requests`, { replace: true });
  }, [customer, slug, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (mode === 'forgot') {
      if (!email.trim()) return;
      setSubmitting(true);
      try {
        await axios.post(`${API}/customer/forgot-password`, { email: email.trim(), help_centre_slug: slug }, { withCredentials: true });
        toast.success('If an account exists, a reset link has been sent to your email.');
        setMode('login');
      } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send reset link'); }
      finally { setSubmitting(false); }
      return;
    }
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/login`, { email: email.trim(), password, help_centre_slug: slug }, { withCredentials: true });
      toast.success('Welcome back');
      await reloadCustomer();
      navigate(`/helpcentre/${slug}/requests`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="hc-login-page">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-6">
        <h1 className="text-xl font-bold text-zinc-900 font-['Outfit'] mb-4">
          {mode === 'forgot' ? 'Reset your password' : `Log in to ${hc.name}`}
        </h1>
        {mode === 'forgot' && <p className="text-xs text-zinc-500 mb-3 -mt-2">Enter your account email and we'll send you a reset link.</p>}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="hc-login-email" />
          </div>
          {mode !== 'forgot' && (
            <div>
              <Label className="text-xs">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="hc-login-password" />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-400 hover:text-zinc-700" aria-label="Toggle password">
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button type="button" onClick={() => setMode('forgot')} className="text-[12px] mt-1.5 font-medium underline" style={{ color: theme }} data-testid="hc-forgot-password-link">
                Forgot password?
              </button>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={submitting} style={{ background: theme, color: 'white' }} data-testid="hc-login-submit">
            {submitting ? 'Please wait…' : (mode === 'forgot' ? 'Send reset link' : 'Sign in')}
          </Button>
        </div>
        {mode === 'forgot' && (
          <p className="text-[12px] text-zinc-600 mt-4 text-center">
            <button type="button" onClick={() => setMode('login')} className="font-semibold underline" style={{ color: theme }} data-testid="hc-back-to-login">Back to log in</button>
          </p>
        )}
      </form>
    </div>
  );
}
